import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  CardHeader,
} from "reactstrap";
import MetaTags from "react-meta-tags";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useHistory, useParams } from "react-router-dom";
import Cropper from "react-easy-crop";
import "../../../Components/Common/crop.css";
import { getDLDealCroppedImg } from "../../../Components/Common/DealCropDL";

const DAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

function UpdateLunchSpecial() {
  const history = useHistory();
  const { dealId } = useParams();
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    price: "",
    categoryId: "",
    days: [],
    dealPhoto: null,
    removePhoto: false,
  });

  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [dealPhotoPreview, setDealPhotoPreview] = useState(null);
  const [selectedFileName, setSelectedFileName] = useState("No file chosen");

  // Cropper states
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);

  /* -------------------- FETCH CATEGORIES -------------------- */
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BASE_URL}categories`);
        const data = await res.json();
        setCategories(data.responseData?.categories || []);
      } catch {
        toast.error("Failed to load categories");
      } finally {
        setCategoriesLoading(false);
      }
    };
    fetchCategories();
  }, []);

  /* -------------------- FETCH DEAL DATA -------------------- */
  useEffect(() => {
    const fetchDeal = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BASE_URL}admin/deal/lunchSpecials/${dealId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            },
          }
        );
        const data = await res.json();
        if (!res.ok) throw new Error(data.responseMessage?.join(", ") || "Failed to fetch deal");

        const deal = data.responseData?.deal;
        setFormData({
          name: deal.name || "",
          description: deal.description || "",
          price: deal.price || "",
          categoryId: deal.categoryId || "",
          days: deal.days || [],
          dealPhoto: null,
          removePhoto: false,
        });
        setDealPhotoPreview(deal.dealPhoto?.url || null);
      } catch (err) {
        toast.error(err.message || "Failed to load deal data");
      }
    };
    if (dealId) fetchDeal();
  }, [dealId]);

  /* -------------------- INPUT HANDLERS -------------------- */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDayToggle = (day) => {
    setFormData((prev) => ({
      ...prev,
      days: prev.days.includes(day)
        ? prev.days.filter((d) => d !== day)
        : [...prev.days, day],
    }));
  };

  /* -------------------- IMAGE CROP -------------------- */
  const onCropComplete = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSelectedFileName(file.name);

    const reader = new FileReader();
    reader.onload = () => {
      setImageToCrop(reader.result);
      setShowCropper(true);
      e.target.value = "";
    };
    reader.readAsDataURL(file);
  };

  const handleCropSave = async () => {
    try {
      const blob = await getDLDealCroppedImg(imageToCrop, croppedAreaPixels);
      const file = new File([blob], "deal-photo.jpg", { type: "image/jpeg" });
      setFormData((prev) => ({ ...prev, dealPhoto: file, removePhoto: false }));
      setDealPhotoPreview(URL.createObjectURL(blob));
      setShowCropper(false);
    } catch {
      toast.error("Image crop failed");
    }
  };

  const handleRemovePhoto = () => {
    setFormData((prev) => ({ ...prev, dealPhoto: null, removePhoto: true }));
    setDealPhotoPreview(null);
    setSelectedFileName("No file chosen");
  };

  /* -------------------- SUBMIT UPDATE -------------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = new FormData();
      if (formData.name) payload.append("name", formData.name);
      if (formData.description) payload.append("description", formData.description);
      if (formData.price) payload.append("price", formData.price);
      if (formData.categoryId) payload.append("categoryId", formData.categoryId);
      payload.append("removePhoto", String(formData.removePhoto));
      formData.days.forEach((d) => payload.append("days[]", d));

      // ------------------ HANDLE EXISTING IMAGE ------------------
      if (formData.dealPhoto) {
        // user uploaded new image
        payload.append("dealPhoto", formData.dealPhoto);
      } else if (dealPhotoPreview && !formData.removePhoto) {
        // user did not upload new image → convert existing URL to File
        const response = await fetch(dealPhotoPreview);
        const blob = await response.blob();
        const file = new File([blob], "existing-deal-photo.jpg", { type: blob.type });
        payload.append("dealPhoto", file);
      }

      const res = await fetch(
        `${process.env.REACT_APP_BASE_URL}admin/deal/lunchSpecials/${dealId}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
          body: payload,
        }
      );

      const data = await res.json();
      if (!res.ok) throw new Error(data.responseMessage?.join(", ") || "Server Error");

      toast.success("Lunch Special updated successfully!");
      setTimeout(() => history.push(`/business-deals/${data.responseData.deal.businessId}`), 1200);
    } catch (err) {
      toast.error(err.message || "Failed to update lunch special");
    } finally {
      setLoading(false);
    }
  };

  /* -------------------- UI -------------------- */
  return (
    <div className="page-content">
      <Container fluid>
        <MetaTags>
          <title>Update Lunch Special</title>
        </MetaTags>
        <ToastContainer />

        <Row>
          <Col lg="12">
            <Card>
              <CardHeader className="d-flex align-items-center">
                <Button color="primary" onClick={() => history.goBack()}>
                  <i className="mdi mdi-arrow-left" />
                </Button>
                <h4 className="mb-0 ms-2">Update Lunch Special</h4>
              </CardHeader>

              <CardBody>
                <Form onSubmit={handleSubmit}>
                  <Row>
                    <Col md={6}>
                      <FormGroup>
                        <Label>Name</Label>
                        <Input name="name" value={formData.name} onChange={handleInputChange} />
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <Label>Price</Label>
                        <div className="input-group">
                          <span className="input-group-text">$</span>
                          <Input
                            name="price"
                            value={formData.price}
                            onChange={(e) =>
                              /^\d*\.?\d*$/.test(e.target.value) &&
                              handleInputChange(e)
                            }
                          />
                        </div>
                      </FormGroup>
                    </Col>
                    <Col md={12}>
                      <FormGroup>
                        <Label>Description</Label>
                        <Input
                          type="textarea"
                          name="description"
                          value={formData.description}
                          onChange={handleInputChange}
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <FormGroup>
                        <Label>Category</Label>
                        <Input
                          type="select"
                          name="categoryId"
                          value={formData.categoryId}
                          onChange={handleInputChange}
                          disabled={categoriesLoading}
                        >
                          <option value="">Select category</option>
                          {categories.map((c) => (
                            <option key={c._id} value={c._id}>{c.name}</option>
                          ))}
                        </Input>
                      </FormGroup>
                    </Col>

                    <Col md={6}>
                      <FormGroup>
                        <Label>Deal Photo</Label>
                        <div className="d-flex align-items-center gap-2">
                          <Button type="button" color="primary" onClick={() => fileInputRef.current.click()}>
                            Choose File
                          </Button>
                          <span>{selectedFileName}</span>
                          {dealPhotoPreview && (
                            <div className="position-relative d-inline-block mt-2 ms-2">
                              <img
                                src={dealPhotoPreview}
                                alt="Deal Preview"
                                className="rounded border"
                                width="100"
                              />
                              <span
                                onClick={handleRemovePhoto}
                                style={{
                                  position: "absolute",
                                  top: "-8px",
                                  right: "-8px",
                                  backgroundColor: "#dc3545",
                                  color: "white",
                                  borderRadius: "50%",
                                  width: "20px",
                                  height: "20px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  cursor: "pointer",
                                  fontWeight: "bold",
                                  fontSize: "14px",
                                  boxShadow: "0 0 3px rgba(0,0,0,0.5)",
                                }}
                              >
                                ×
                              </span>
                            </div>
                          )}
                        </div>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handleFileChange}
                          hidden
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={12}>
                      <Label>Days</Label>
                      <div className="d-flex flex-wrap gap-2">
                        {DAYS.map((day) => (
                          <FormGroup check key={day}>
                            <Input
                              type="checkbox"
                              checked={formData.days.includes(day)}
                              onChange={() => handleDayToggle(day)}
                            />
                            <Label check>{day}</Label>
                          </FormGroup>
                        ))}
                      </div>
                    </Col>
                  </Row>

                  <Button type="submit" color="primary" className="mt-4" disabled={loading}>
                    {loading ? "Updating..." : "Update"}
                  </Button>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {showCropper && (
          <div className="crop-modal">
            <div className="crop-modal-content">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h4>Crop Image</h4>
                <button
                  type="button"
                  className="btn btn-danger btn-sm"
                  onClick={() => setShowCropper(false)}
                >
                  &times;
                </button>
              </div>

              <div className="crop-container">
                <Cropper
                  image={imageToCrop}
                  crop={crop}
                  zoom={zoom}
                  aspect={3 / 2}
                  onCropChange={setCrop}
                  onCropComplete={onCropComplete}
                  onZoomChange={setZoom}
                />
              </div>

              <div className="crop-controls">
                <label>Zoom</label>
                <input
                  type="range"
                  min={1}
                  max={3}
                  step={0.1}
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                />
              </div>

              <div className="crop-buttons">
                <button className="btn btn-secondary" onClick={() => setShowCropper(false)}>
                  Cancel
                </button>
                <button className="btn btn-primary" onClick={handleCropSave}>
                  Save Crop
                </button>
              </div>
            </div>
          </div>
        )}
      </Container>
    </div>
  );
}

export default UpdateLunchSpecial;
